/**
 * /equipment/{id} — Equipment detail with tabs.
 *
 * Tabs:
 *   Overview     read-only summary + Edit (non-rate fields)
 *   Rates        current rates + audit-trailed edit + history table
 *   Time entries paginated entries for this item with posting status
 *   Linked asset (owned only) FixedAsset summary card
 */
import {
  createFileRoute,
  Link,
  useNavigate,
} from "@tanstack/react-router";
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import {
  equipmentItemDetailQueryOptions,
  equipmentRateHistoryQueryOptions,
  equipmentRentalLinksForItemQueryOptions,
  equipmentRentalLinkKeys,
  equipmentTimeEntriesQueryOptions,
} from "@/lib/queries";
import type {
  EquipmentItem,
  EquipmentRentalLink,
  EquipmentTimeEntry,
} from "@/lib/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { HelpHint } from "@/components/HelpHint";
import { EquipmentFormDialog } from "@/components/EquipmentFormDialog";
import { EquipmentRatesDialog } from "@/components/EquipmentRatesDialog";
import { MoneyInput } from "@/components/MoneyInput";
import { RentalLinkDialog } from "@/components/RentalLinkDialog";
import AddIcon from "@mui/icons-material/Add";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { useToast } from "@/components/Toaster";

export const Route = createFileRoute("/_auth/equipment/$equipmentId")({
  component: EquipmentDetailPage,
});

interface FixedAssetDetail {
  id: string;
  asset_no: string;
  description: string;
  cost: string;
  salvage_value: string;
  status: string;
  acquired_date: string;
  in_service_date: string | null;
}

function fmtMoney(v: string | number | null | undefined) {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n as number);
}

function chargeOut(it: EquipmentItem): string | null {
  if (!it.ownership_rate && !it.operating_rate_est) return null;
  const own = Number(it.ownership_rate ?? 0);
  const op = Number(it.operating_rate_est ?? 0);
  return String(own + op);
}

function EquipmentDetailPage() {
  const { equipmentId } = Route.useParams();
  const navigate = useNavigate();

  const [tab, setTab] = useState<
    "overview" | "rates" | "time" | "asset" | "rentals" | "service" | "maintenance"
  >("overview");
  const [editOpen, setEditOpen] = useState(false);
  const [ratesOpen, setRatesOpen] = useState(false);
  const [rentalDialogOpen, setRentalDialogOpen] = useState(false);

  const itemQ = useQuery(equipmentItemDetailQueryOptions(equipmentId));
  const item = itemQ.data;

  if (itemQ.isLoading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!item) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Equipment not found.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate({ to: "/equipment" })}
            size="small"
          >
            Back
          </Button>
          <Typography variant="h5" fontWeight={600}>
            <span style={{ fontFamily: "monospace" }}>{item.code}</span> ·{" "}
            {item.name}
          </Typography>
          <Chip
            size="small"
            label={item.ownership_type}
            color={item.ownership_type === "owned" ? "primary" : "default"}
            variant="outlined"
          />
          {!item.is_active && (
            <Chip size="small" label="Inactive" color="warning" />
          )}
        </Stack>
        <HelpHint
          docPath="equipment-costing/README.md"
          label="Equipment help"
        />
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        <Tab label="Overview" value="overview" />
        <Tab label="Rates" value="rates" />
        <Tab label="Time entries" value="time" />
        <Tab label="Service / Fuel" value="service" />
        <Tab label="PM Schedules" value="maintenance" />
        {item.ownership_type === "owned" && (
          <Tab label="Linked asset" value="asset" />
        )}
        {(item.ownership_type === "rented" ||
          item.ownership_type === "leased") && (
          <Tab label="Rentals" value="rentals" />
        )}
      </Tabs>

      {tab === "overview" && (
        <OverviewTab item={item} onEdit={() => setEditOpen(true)} />
      )}
      {tab === "rates" && (
        <RatesTab
          item={item}
          onEditRates={() => setRatesOpen(true)}
          equipmentId={equipmentId}
        />
      )}
      {tab === "time" && <TimeEntriesTab equipmentId={equipmentId} />}
      {tab === "service" && <ServiceEntriesTab equipmentId={equipmentId} />}
      {tab === "maintenance" && <MaintenanceTab equipmentId={equipmentId} />}
      {tab === "asset" && item.fixed_asset_id && (
        <LinkedAssetTab fixedAssetId={item.fixed_asset_id} />
      )}
      {tab === "rentals" && (
        <RentalsTab
          equipmentId={equipmentId}
          onAdd={() => setRentalDialogOpen(true)}
        />
      )}

      <EquipmentFormDialog
        open={editOpen}
        mode="edit"
        equipmentId={equipmentId}
        onClose={() => setEditOpen(false)}
      />
      <EquipmentRatesDialog
        open={ratesOpen}
        item={item}
        onClose={() => setRatesOpen(false)}
      />
      <RentalLinkDialog
        open={rentalDialogOpen}
        equipmentId={equipmentId}
        onClose={() => setRentalDialogOpen(false)}
      />
    </Box>
  );
}

function RentalsTab({
  equipmentId,
  onAdd,
}: {
  equipmentId: string;
  onAdd: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const linksQ = useQuery(
    equipmentRentalLinksForItemQueryOptions(equipmentId, { limit: 200 }),
  );
  const links: EquipmentRentalLink[] = linksQ.data?.items ?? [];

  const unlinkMut = useMutation({
    mutationFn: (id: string) =>
      api(`/api/construction/equipment/rental-links/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Rental link removed");
      qc.invalidateQueries({ queryKey: equipmentRentalLinkKeys.all });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h6">Rentals</Typography>
          <HelpHint
            docPath="equipment-costing/rental-linkage.md"
            label="Rental linkage help"
          />
        </Stack>
        <Button startIcon={<AddIcon />} variant="contained" onClick={onAdd}>
          Link AP line
        </Button>
      </Stack>

      <Paper variant="outlined">
        {linksQ.isLoading && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {!linksQ.isLoading && links.length === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No rental links yet. Use "Link AP line" to attribute an AP
              invoice line to this equipment item so the cost shows up on
              the cost-by-job report.
            </Typography>
          </Box>
        )}
        {links.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Rental start</TableCell>
                <TableCell>Rental end</TableCell>
                <TableCell>AP invoice</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Cost code</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {links.map((l) => (
                <TableRow key={l.id} hover>
                  <TableCell>{l.rental_start}</TableCell>
                  <TableCell>{l.rental_end ?? "—"}</TableCell>
                  <TableCell>
                    <Link
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      to={`/invoices/ap/${l.ap_invoice_id}` as any}
                      style={{ color: "inherit" }}
                    >
                      {l.ap_invoice_no ?? l.ap_invoice_id.slice(0, 8) + "…"}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {l.vendor_name ?? "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {l.project_code ?? "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {l.cost_code ?? "—"}
                  </TableCell>
                  <TableCell align="right">{fmtMoney(l.amount)}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                    {l.notes ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color="warning"
                      startIcon={<LinkOffIcon />}
                      disabled={unlinkMut.isPending}
                      onClick={() => unlinkMut.mutate(l.id)}
                    >
                      Unlink
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Box sx={{ minWidth: 200 }}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="body2">{value ?? "—"}</Typography>
    </Box>
  );
}

function OverviewTab({
  item,
  onEdit,
}: {
  item: EquipmentItem;
  onEdit: () => void;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h6">Overview</Typography>
        <Button startIcon={<EditIcon />} onClick={onEdit} size="small">
          Edit
        </Button>
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
        <Field label="Code" value={item.code} />
        <Field label="Name" value={item.name} />
        <Field label="Category" value={item.category} />
        <Field label="Ownership" value={item.ownership_type} />
        <Field label="Active" value={item.is_active ? "Yes" : "No"} />
        <Field label="Consumable" value={item.is_consumable ? "Yes" : "No"} />
        <Field label="Year" value={item.year} />
        <Field label="Make" value={item.make} />
        <Field label="Model" value={item.model} />
        <Field label="VIN" value={item.vin} />
        <Field label="License plate" value={item.license_plate} />
        <Field label="Planned annual hrs" value={item.planned_annual_hours} />
        <Field
          label="Linked fixed asset"
          value={item.fixed_asset_id ? "Linked" : "—"}
        />
        <Field
          label="Clearing account"
          value={item.gl_clearing_account_id ? "Set" : "—"}
        />
        <Field
          label="Operating account"
          value={item.gl_operating_account_id ? "Set" : "—"}
        />
      </Box>
      {item.description && (
        <Box mt={3}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Description
          </Typography>
          <Typography variant="body2">{item.description}</Typography>
        </Box>
      )}
    </Paper>
  );
}

function RatesTab({
  item,
  onEditRates,
  equipmentId,
}: {
  item: EquipmentItem;
  onEditRates: () => void;
  equipmentId: string;
}) {
  const histQ = useQuery(equipmentRateHistoryQueryOptions(equipmentId));
  const history = histQ.data ?? [];

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h6">Current rates</Typography>
          <Button
            startIcon={<EditIcon />}
            onClick={onEditRates}
            size="small"
            variant="contained"
          >
            Edit rates
          </Button>
        </Stack>
        <Box
          sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}
        >
          <Field
            label="Ownership rate"
            value={fmtMoney(item.ownership_rate) + "/hr"}
          />
          <Field
            label="Operating rate (est)"
            value={fmtMoney(item.operating_rate_est) + "/hr"}
          />
          <Field
            label="Charge-out (computed)"
            value={
              chargeOut(item) ? fmtMoney(chargeOut(item)) + "/hr" : "—"
            }
          />
          <Field label="Idle rate" value={fmtMoney(item.idle_rate) + "/hr"} />
          <Field
            label="Transport rate"
            value={fmtMoney(item.transport_rate) + "/mile"}
          />
          <Field
            label="Planned annual hrs"
            value={item.planned_annual_hours ?? "—"}
          />
        </Box>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", mt: 2 }}
        >
          Rate edits write an audit row and only affect time entries created
          from now on. Existing entries keep their frozen rate snapshot.
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" mb={2}>
          Rate history
        </Typography>
        {histQ.isLoading && <CircularProgress size={20} />}
        {!histQ.isLoading && history.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No rate changes recorded yet.
          </Typography>
        )}
        {history.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell align="right">Old</TableCell>
                <TableCell align="right">New</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Changed at</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((h) => (
                <TableRow key={h.id} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                    {h.field_name}
                  </TableCell>
                  <TableCell align="right">{fmtMoney(h.old_value)}</TableCell>
                  <TableCell align="right">{fmtMoney(h.new_value)}</TableCell>
                  <TableCell>{h.reason ?? "—"}</TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>
                    {new Date(h.changed_at).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}

function TimeEntriesTab({ equipmentId }: { equipmentId: string }) {
  const entriesQ = useQuery(
    equipmentTimeEntriesQueryOptions(equipmentId, { limit: 200 }),
  );
  const entries: EquipmentTimeEntry[] = entriesQ.data ?? [];

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6" mb={2}>
        Time entries
      </Typography>
      {entriesQ.isLoading && <CircularProgress size={20} />}
      {entries.length === 0 && !entriesQ.isLoading && (
        <Typography variant="body2" color="text.secondary">
          No time entries yet.
        </Typography>
      )}
      {entries.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Work date</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Cost code</TableCell>
              <TableCell align="right">Run hrs</TableCell>
              <TableCell align="right">Idle hrs</TableCell>
              <TableCell align="right">Transport mi</TableCell>
              <TableCell align="right">Ownership $</TableCell>
              <TableCell align="right">Operating $</TableCell>
              <TableCell align="right">Transport $</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((e) => (
              <TableRow key={e.id} hover>
                <TableCell>{e.work_date}</TableCell>
                <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                  {e.project_id ? e.project_id.slice(0, 8) + "…" : "—"}
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                  {e.cost_code_id ? e.cost_code_id.slice(0, 8) + "…" : "—"}
                </TableCell>
                <TableCell align="right">{e.run_hours}</TableCell>
                <TableCell align="right">{e.idle_hours}</TableCell>
                <TableCell align="right">{e.transport_miles}</TableCell>
                <TableCell align="right">
                  {fmtMoney(e.ownership_amount)}
                </TableCell>
                <TableCell align="right">
                  {fmtMoney(e.operating_amount)}
                </TableCell>
                <TableCell align="right">
                  {fmtMoney(e.transport_amount)}
                </TableCell>
                <TableCell>
                  {e.posting_id ? (
                    <Link
                      to="/equipment/cost-postings/$postingId"
                      params={{ postingId: e.posting_id }}
                      style={{ color: "inherit" }}
                    >
                      <Chip
                        size="small"
                        color="success"
                        label="Posted"
                        variant="outlined"
                      />
                    </Link>
                  ) : (
                    <Chip
                      size="small"
                      label="Unposted"
                      variant="outlined"
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

// ─── Service Entries Tab ──────────────────────────────────────────────────────

interface ServiceEntry {
  id: string;
  equipment_id: string;
  project_id: string | null;
  service_date: string;
  entry_type: string;
  cost_component: string;
  description: string | null;
  quantity: string | null;
  unit_cost: string | null;
  total_cost: string;
  odometer_reading: number | null;
  hours_at_service: string | null;
  notes: string | null;
  created_at: string;
}

const ENTRY_TYPE_LABELS: Record<string, string> = {
  fuel: "Fuel",
  repair: "Repair",
  inspection: "Inspection",
  fixed_cost: "Fixed cost",
};

function ServiceEntriesTab({ equipmentId }: { equipmentId: string }) {
  const qc = useQueryClient();
  const toast = useToast();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    service_date: new Date().toISOString().slice(0, 10),
    entry_type: "fuel",
    cost_component: "operating",
    description: "",
    quantity: "",
    unit_cost: "",
    total_cost: "",
    hours_at_service: "",
    notes: "",
  });

  const entriesQ = useQuery({
    queryKey: ["equipment-service-entries", equipmentId],
    queryFn: () =>
      api<ServiceEntry[]>(
        `/api/construction/equipment/${equipmentId}/service-entries?limit=200`,
      ),
  });
  const entries = entriesQ.data ?? [];

  const createMut = useMutation({
    mutationFn: (body: object) =>
      api(`/api/construction/equipment/service-entries`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      toast.success("Service entry recorded");
      qc.invalidateQueries({ queryKey: ["equipment-service-entries", equipmentId] });
      setShowForm(false);
      setForm({
        service_date: new Date().toISOString().slice(0, 10),
        entry_type: "fuel",
        cost_component: "operating",
        description: "",
        quantity: "",
        unit_cost: "",
        total_cost: "",
        hours_at_service: "",
        notes: "",
      });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      api(`/api/construction/equipment/service-entries/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Entry deleted");
      qc.invalidateQueries({ queryKey: ["equipment-service-entries", equipmentId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: Record<string, unknown> = {
      equipment_id: equipmentId,
      service_date: form.service_date,
      entry_type: form.entry_type,
      cost_component: form.cost_component,
      total_cost: form.total_cost || "0",
    };
    if (form.description) payload.description = form.description;
    if (form.quantity) payload.quantity = form.quantity;
    if (form.unit_cost) payload.unit_cost = form.unit_cost;
    if (form.hours_at_service) payload.hours_at_service = form.hours_at_service;
    if (form.notes) payload.notes = form.notes;
    createMut.mutate(payload);
  }

  const componentColor: Record<string, "info" | "warning"> = {
    operating: "info",
    ownership: "warning",
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">Service / Fuel entries</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => setShowForm((s) => !s)}
        >
          {showForm ? "Cancel" : "Add entry"}
        </Button>
      </Stack>

      {showForm && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" mb={1}>New service entry</Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}
          >
            <Box>
              <Typography variant="caption">Date *</Typography>
              <input
                type="date"
                value={form.service_date}
                onChange={(e) => setForm((f) => ({ ...f, service_date: e.target.value }))}
                required
                style={{ display: "block", width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4 }}
              />
            </Box>
            <Box>
              <Typography variant="caption">Entry type *</Typography>
              <select
                value={form.entry_type}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  entry_type: e.target.value,
                  cost_component: e.target.value === "fixed_cost" ? "ownership" : "operating",
                }))}
                style={{ display: "block", width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4 }}
              >
                <option value="fuel">Fuel</option>
                <option value="repair">Repair</option>
                <option value="inspection">Inspection</option>
                <option value="fixed_cost">Fixed cost</option>
              </select>
            </Box>
            <Box>
              <Typography variant="caption">Cost component</Typography>
              <select
                value={form.cost_component}
                onChange={(e) => setForm((f) => ({ ...f, cost_component: e.target.value }))}
                style={{ display: "block", width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4 }}
              >
                <option value="operating">Operating</option>
                <option value="ownership">Ownership</option>
              </select>
            </Box>
            <Box>
              <Typography variant="caption">Description</Typography>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="e.g. Fuel fill — 45 gal diesel"
                style={{ display: "block", width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4 }}
              />
            </Box>
            <Box>
              <Typography variant="caption">Qty (gal / units)</Typography>
              <input
                type="number"
                step="0.001"
                value={form.quantity}
                onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                style={{ display: "block", width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4 }}
              />
            </Box>
            <Box>
              <Typography variant="caption">Unit cost</Typography>
              <MoneyInput
                value={form.unit_cost}
                onChange={(v) => setForm((f) => ({ ...f, unit_cost: v }))}
                className="border-[#ccc] rounded px-2 py-1.5 text-[13px]"
              />
            </Box>
            <Box>
              <Typography variant="caption">Total cost *</Typography>
              <MoneyInput
                value={form.total_cost}
                onChange={(v) => setForm((f) => ({ ...f, total_cost: v }))}
                required
                className="border-[#ccc] rounded px-2 py-1.5 text-[13px]"
              />
            </Box>
            <Box>
              <Typography variant="caption">Hours at service</Typography>
              <input
                type="number"
                step="0.01"
                value={form.hours_at_service}
                onChange={(e) => setForm((f) => ({ ...f, hours_at_service: e.target.value }))}
                placeholder="Current meter reading"
                style={{ display: "block", width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4 }}
              />
            </Box>
            <Box>
              <Typography variant="caption">Notes</Typography>
              <input
                type="text"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                style={{ display: "block", width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4 }}
              />
            </Box>
            <Box sx={{ gridColumn: "span 3" }}>
              <Button
                type="submit"
                variant="contained"
                disabled={createMut.isPending || !form.total_cost}
              >
                {createMut.isPending ? "Saving…" : "Save entry"}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      <Paper variant="outlined">
        {entriesQ.isLoading && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {!entriesQ.isLoading && entries.length === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No service entries yet. Log fuel fills, repairs, and inspections here to power the two-variance report.
            </Typography>
          </Box>
        )}
        {entries.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Component</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Unit $</TableCell>
                <TableCell align="right">Total $</TableCell>
                <TableCell align="right">Hrs</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell>{e.service_date}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={ENTRY_TYPE_LABELS[e.entry_type] ?? e.entry_type}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={e.cost_component}
                      color={componentColor[e.cost_component] ?? "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{e.description ?? "—"}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>
                    {e.quantity ?? "—"}
                  </TableCell>
                  <TableCell align="right">{e.unit_cost ? fmtMoney(e.unit_cost) : "—"}</TableCell>
                  <TableCell align="right">{fmtMoney(e.total_cost)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>
                    {e.hours_at_service ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color="error"
                      disabled={deleteMut.isPending}
                      onClick={() => deleteMut.mutate(e.id)}
                    >
                      Del
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}

// ─── Maintenance (PM) Schedules Tab ──────────────────────────────────────────

interface MaintenanceSchedule {
  id: string;
  equipment_id: string;
  description: string;
  interval_type: string;
  interval_value: number;
  last_service_date: string | null;
  last_service_hours: string | null;
  next_due_date: string | null;
  next_due_hours: string | null;
  is_active: boolean;
  is_overdue: boolean;
  notes: string | null;
}

function MaintenanceTab({ equipmentId }: { equipmentId: string }) {
  const qc = useQueryClient();
  const toast = useToast();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    description: "",
    interval_type: "hours",
    interval_value: "",
    last_service_date: "",
    last_service_hours: "",
    notes: "",
  });

  const schedQ = useQuery({
    queryKey: ["equipment-maintenance-schedules", equipmentId],
    queryFn: () =>
      api<MaintenanceSchedule[]>(
        `/api/construction/equipment/${equipmentId}/maintenance-schedules?active_only=false`,
      ),
  });
  const schedules = schedQ.data ?? [];

  const createMut = useMutation({
    mutationFn: (body: object) =>
      api(`/api/construction/equipment/maintenance-schedules`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      toast.success("PM schedule created");
      qc.invalidateQueries({ queryKey: ["equipment-maintenance-schedules", equipmentId] });
      setShowForm(false);
      setForm({ description: "", interval_type: "hours", interval_value: "", last_service_date: "", last_service_hours: "", notes: "" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      api(`/api/construction/equipment/maintenance-schedules/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Schedule deleted");
      qc.invalidateQueries({ queryKey: ["equipment-maintenance-schedules", equipmentId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: Record<string, unknown> = {
      equipment_id: equipmentId,
      description: form.description,
      interval_type: form.interval_type,
      interval_value: Number(form.interval_value),
    };
    if (form.last_service_date) payload.last_service_date = form.last_service_date;
    if (form.last_service_hours) payload.last_service_hours = form.last_service_hours;
    if (form.notes) payload.notes = form.notes;
    createMut.mutate(payload);
  }

  const overdueCount = schedules.filter((s) => s.is_overdue).length;

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">PM Schedules</Typography>
          {overdueCount > 0 && (
            <Chip
              size="small"
              label={`${overdueCount} overdue`}
              color="error"
            />
          )}
        </Stack>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => setShowForm((s) => !s)}
        >
          {showForm ? "Cancel" : "Add schedule"}
        </Button>
      </Stack>

      {showForm && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" mb={1}>New PM schedule</Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}
          >
            <Box sx={{ gridColumn: "span 2" }}>
              <Typography variant="caption">Description *</Typography>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                required
                placeholder="e.g. Engine oil change"
                style={{ display: "block", width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4 }}
              />
            </Box>
            <Box>
              <Typography variant="caption">Interval type *</Typography>
              <select
                value={form.interval_type}
                onChange={(e) => setForm((f) => ({ ...f, interval_type: e.target.value }))}
                style={{ display: "block", width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4 }}
              >
                <option value="hours">Hours</option>
                <option value="days">Days</option>
                <option value="months">Months</option>
              </select>
            </Box>
            <Box>
              <Typography variant="caption">Interval value *</Typography>
              <input
                type="number"
                min="1"
                step="1"
                value={form.interval_value}
                onChange={(e) => setForm((f) => ({ ...f, interval_value: e.target.value }))}
                required
                placeholder={form.interval_type === "hours" ? "e.g. 250" : form.interval_type === "days" ? "e.g. 90" : "e.g. 6"}
                style={{ display: "block", width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4 }}
              />
            </Box>
            <Box>
              <Typography variant="caption">Last service date</Typography>
              <input
                type="date"
                value={form.last_service_date}
                onChange={(e) => setForm((f) => ({ ...f, last_service_date: e.target.value }))}
                style={{ display: "block", width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4 }}
              />
            </Box>
            {form.interval_type === "hours" && (
              <Box>
                <Typography variant="caption">Last service hours</Typography>
                <input
                  type="number"
                  step="0.01"
                  value={form.last_service_hours}
                  onChange={(e) => setForm((f) => ({ ...f, last_service_hours: e.target.value }))}
                  placeholder="Current meter reading at last service"
                  style={{ display: "block", width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4 }}
                />
              </Box>
            )}
            <Box sx={{ gridColumn: "span 3" }}>
              <Button
                type="submit"
                variant="contained"
                disabled={createMut.isPending || !form.description || !form.interval_value}
              >
                {createMut.isPending ? "Saving…" : "Save schedule"}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      <Paper variant="outlined">
        {schedQ.isLoading && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {!schedQ.isLoading && schedules.length === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No PM schedules yet. Add oil change intervals, filter replacements, and inspection cycles here.
            </Typography>
          </Box>
        )}
        {schedules.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Interval</TableCell>
                <TableCell>Last service</TableCell>
                <TableCell>Next due</TableCell>
                <TableCell>Status</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map((s) => (
                <TableRow key={s.id} hover sx={s.is_overdue ? { bgcolor: "error.50" } : undefined}>
                  <TableCell>{s.description}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    Every {s.interval_value} {s.interval_type}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {s.last_service_date ?? "—"}
                    {s.last_service_hours ? ` (${s.last_service_hours} hrs)` : ""}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {s.next_due_date ?? ""}
                    {s.next_due_hours ? ` ≥ ${s.next_due_hours} hrs` : ""}
                    {!s.next_due_date && !s.next_due_hours ? "—" : ""}
                  </TableCell>
                  <TableCell>
                    {!s.is_active ? (
                      <Chip size="small" label="Inactive" variant="outlined" />
                    ) : s.is_overdue ? (
                      <Chip size="small" label="Overdue" color="error" />
                    ) : (
                      <Chip size="small" label="Active" color="success" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color="error"
                      disabled={deleteMut.isPending}
                      onClick={() => deleteMut.mutate(s.id)}
                    >
                      Del
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}

function LinkedAssetTab({ fixedAssetId }: { fixedAssetId: string }) {
  const assetQ = useQuery({
    queryKey: ["fixed-asset", fixedAssetId],
    queryFn: async () => {
      // /api/assets returns the list — find ours.
      const list = await api<FixedAssetDetail[]>("/api/assets");
      return list.find((a) => a.id === fixedAssetId) ?? null;
    },
    enabled: !!fixedAssetId,
  });

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h6">Linked fixed asset</Typography>
        <Button
          component={Link}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          to={"/assets" as any}
          size="small"
        >
          Open in Fixed Assets →
        </Button>
      </Stack>
      {assetQ.isLoading && <CircularProgress size={20} />}
      {assetQ.data ? (
        <Box
          sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}
        >
          <Field label="Asset #" value={assetQ.data.asset_no} />
          <Field label="Description" value={assetQ.data.description} />
          <Field label="Status" value={assetQ.data.status} />
          <Field label="Cost" value={fmtMoney(assetQ.data.cost)} />
          <Field
            label="Salvage value"
            value={fmtMoney(assetQ.data.salvage_value)}
          />
          <Field label="Acquired" value={assetQ.data.acquired_date} />
          <Field
            label="In service"
            value={assetQ.data.in_service_date ?? "—"}
          />
        </Box>
      ) : (
        !assetQ.isLoading && (
          <Alert severity="warning">
            Linked fixed asset not found in /api/assets — has it been
            deleted? Update the equipment item to clear or relink.
          </Alert>
        )
      )}
      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Depreciation runs on this asset via the Fixed Assets module.
        Equipment costing only uses the link to wire ownership cost back to
        the right balance-sheet asset.
      </Typography>
    </Paper>
  );
}
